import {useRef, useEffect, useState} from 'react'
import logger from '@/utils/logger'

const EMPTY_SOURCES = Object.freeze([])

function findMatchingSource(sources = []) {
  if (typeof window === 'undefined') {
    return sources.find(source => !source.media) ?? sources[0]
  }

  return (
    sources.find(
      source => source.media && window.matchMedia(source.media).matches,
    ) ??
    sources.find(source => !source.media) ??
    sources[0]
  )
}

/**
 * @typedef {Object} HLSVideoSource
 * @property {string} src
 * @property {string} [media]
 * @property {string} [poster]
 * @property {string} [type]
 */

/**
 * @typedef {import('react').VideoHTMLAttributes<HTMLVideoElement> & {
 *   src?: string,
 *   sources?: HLSVideoSource[],
 *   lazy?: boolean,
 * }} HLSVideoProps
 */

/**
 * @param {HLSVideoProps} props
 */
function HLSVideo({
  src,
  sources = EMPTY_SOURCES,
  autoPlay,
  lazy = false,
  ...rest
}) {
  const ref = useRef()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!lazy || visible) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true)
        observer.disconnect()
      }
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [lazy, visible])

  useEffect(() => {
    if (lazy && !visible && typeof IntersectionObserver !== 'undefined') return
    const video = ref.current
    const selectedSource = findMatchingSource(sources)
    const videoSrc = selectedSource?.src ?? src

    if (!video || !videoSrc) {
      return undefined
    }

    if (selectedSource?.poster) {
      video.poster = selectedSource.poster
    }

    const sourceType = selectedSource?.type
    const isHlsSource =
      sourceType === 'application/vnd.apple.mpegurl' ||
      sourceType === 'application/x-mpegURL' ||
      /\.m3u8(?:$|[?#])/i.test(videoSrc)

    let cancelled = false
    let hls

    // hls.js over Media Source Extensions: the player for HLS wherever the
    // browser cannot play the stream itself.
    const playWithHlsJs = () => {
      import('hls.js')
        .then(({default: Hls}) => {
          // The user may navigate away or change the source during the import.
          if (cancelled || !Hls.isSupported()) return
          hls = new Hls()
          if (autoPlay) {
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              if (!cancelled) video.play()?.catch(() => {})
            })
          }
          hls.loadSource(videoSrc)
          hls.attachMedia(video)
        })
        .catch(error => {
          if (!cancelled) {
            logger.error(error, {
              description: 'Unable to load HLS playback',
              source: 'HLSVideo',
            })
          }
        })
    }

    // MP4 and native HLS should never pay the download and parse cost of the
    // JavaScript streaming player, so a browser that says it can play HLS
    // (Safari, iOS) gets the stream directly. Recent Chrome also answers
    // "maybe" yet can fail to decode the stream (MEDIA_ERR_SRC_NOT_SUPPORTED),
    // which left the film on its poster: a native HLS error falls back to
    // hls.js.
    const nativeHls = isHlsSource
      ? video.canPlayType('application/vnd.apple.mpegurl')
      : ''
    if (!isHlsSource || nativeHls) {
      const fallBack = () => {
        if (cancelled) return
        video.removeAttribute('src')
        video.load()
        playWithHlsJs()
      }
      if (isHlsSource) {
        video.addEventListener('error', fallBack, {once: true})
      }
      video.src = videoSrc
      if (autoPlay) {
        video.play()?.catch(() => {})
      }
      return () => {
        cancelled = true
        video.removeEventListener('error', fallBack)
        hls?.destroy()
      }
    }

    playWithHlsJs()

    return () => {
      cancelled = true
      hls?.destroy()
    }
  }, [src, sources, autoPlay, lazy, visible])

  // Sources are attached by the effect so hidden/lazy players cannot start a
  // native download before their viewport and visibility have been checked.
  return <video ref={ref} autoPlay={autoPlay} {...rest} />
}

export default HLSVideo
